import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@/design/tokens.css';
import { loadRuntimeConfig } from '@/lib/runtimeConfig';
import { completeMsalBridgeIfNeeded, completeMsalRedirectIfNeeded } from '@/auth/AuthAdapter';
import { App } from '@/App';

async function bootstrap() {
  // First, and before anything else — not even config.json — because this
  // page load might not be a real visit at all, just MSAL's popup or
  // redirect bouncing an auth response back to the app. If so, this page's
  // only job is relaying that response (AuthAdapter.ts's own remarks on
  // completeMsalBridgeIfNeeded explain why); rendering the app on top of it
  // is exactly the bug that was found live, testing a real sign-in.
  if (await completeMsalBridgeIfNeeded()) return;

  // `/config.json` is read before anything else so tenant, client id and
  // `Auth:LocalSignInEnabled` come from the environment, not from the bundle.
  await loadRuntimeConfig();

  // Completes a sign-in that fell back to a full-page redirect (a blocked
  // popup — see AuthAdapter.ts's signInWithSso) before AuthProvider ever
  // mounts and calls restore(): MSAL needs this run once, on the page Entra
  // redirects back to, to consume the auth code in the URL and populate its
  // cache. A no-op on every other load (nothing in the URL to consume) and
  // in mock mode (mirrors createAuthAdapter's own VITE_USE_MOCKS switch).
  await completeMsalRedirectIfNeeded();

  if (import.meta.env.VITE_USE_MOCKS !== 'false') {
    const { startMockWorker } = await import('@/mocks/browser');
    await startMockWorker();
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
